import DamSireAPI from "@/app/apis/main/dam-sire/dam-sire-api.js";

export default {
    /**
     * Adds a new Dam/Sire by delegating to the DamSireAPI service.
     *
     * @async
     * @function addDamSire
     * @param {Object} damSireDetails - The details of the Dam/Sire to be added.
     * @param {Object} damSireDetails.formData - The form data for the Dam/Sire.
     * @param {File} [damSireDetails.dogImageUpload] - The file for the dog image (optional).
     * @returns {Promise<Object>} The response from the DamSireAPI service.
     * @throws {Error} If the API request fails.
     */
    async addDamSire(damSireDetails) {
        return DamSireAPI.addDamSire(damSireDetails);
    },

    /**
     * Fetches the details of a Dam/Sire by delegating to the DamSireAPI service.
     *
     * @async
     * @function getDamSireDetails
     * @param {number} damSireId - The ID of the Dam/Sire to retrieve.
     * @returns {Promise<Object>} The response from the DamSireAPI service containing the Dam/Sire details.
     * @throws {Error} If the API request fails.
     */
    async getDamSireDetails(damSireId) {
        return DamSireAPI.getDamSireDetails(damSireId);
    },

    /**
     * Updates an existing Dam/Sire by delegating to the DamSireAPI service.
     *
     * @async
     * @function updateDamSire
     * @param {number} damSireId - The ID of the Dam/Sire to be updated.
     * @param {Object} damSireDetails - The updated details of the Dam/Sire.
     * @param {Object} damSireDetails.formData - The form data for the Dam/Sire.
     * @param {File} [damSireDetails.dogImageUpload] - The file for the dog image (optional).
     * @returns {Promise<Object>} The response from the DamSireAPI service.
     * @throws {Error} If the API request fails.
     */
    async updateDamSire(damSireId, damSireDetails) {
        return DamSireAPI.updateDamSire(damSireId, damSireDetails);
    },


    /**
     * Retrieves the whelp types and previous litters for a given dam.
     *
     * @param {string} damId - The unique identifier of the dam.
     * @returns {Promise<Object>} A promise that resolves to the response data.
     */
    async getDamWhelpTypesAndPrevLitters(damId) {
        return DamSireAPI.getDamWhelpTypesAndPrevLitters(damId);
    },

	// *********************************** DOCUMENTS ****************************************************
    /**
     * Adds a new document by sending a POST request with form data.
     *
     * @async
     * @function uploadDamSireDocument
     * @param {Object} uploadDetails - The details of the Dam/Sire to be added.
     * @param {Object} uploadDetails.formData - The form data for the Dam/Sire.
     * @param {File} [uploadDetails.document] - The file for the document (required).
     * @returns {Promise<Object>} The response from the API.
     * @throws {Error} If the API request fails.
     */
    async uploadDamSireDocument(uploadDetails) {
        return DamSireAPI.uploadDamSireDocument(uploadDetails);
    },

    /**
     * Uploads multiple dam/sire documents to the server.
     *
     * @async
     * @function uploadMultipleDamSireDocuments
     * @param {Object} uploadDetails - The details of the documents to be uploaded, including file data and associated metadata.
     * @returns {Promise<Object>} A promise that resolves to the server response after uploading the documents.
     * @throws {Error} Throws an error if the upload process fails.
     */
    async uploadMultipleDamSireDocuments(uploadDetails) {
        return DamSireAPI.uploadMultipleDamSireDocuments(uploadDetails);
    },

    /**
     * Fetches the list of Dam/Sire documents based on the provided query parameters.
     *
     * @async
     * @function getDamSireDocumentsList
     * @param {Object} queryParams - The query parameters used to filter or paginate the document list.
     * @param {string} [queryParams.damSireId] - The ID of the Dam/Sire to filter documents.
     * @param {string} [queryParams.sortBy] - The field to sort by (e.g., 'createdAt').
     * @param {string} [queryParams.filterBy] - The field to sort by (e.g., 'fileName', 'fileType').
     * @param {string} [queryParams.order] - The sort order ('asc' or 'desc').
     * @param {number} [queryParams.page] - The page number for pagination.
     * @param {number} [queryParams.limit] - The number of documents to retrieve per page.
     * @returns {Promise<Object>} The response from the API containing the list of documents.
     * @throws {Error} If the API request fails.
    */
    async getDamSireDocumentsList(queryParams) {
        return DamSireAPI.getDamSireDocumentsList(queryParams);
    },

    /**
     * Retrieves the progress of dam/sire documents based on the provided status.
     *
     * @async
     * @function getDamSireDocumentsListProgress
     * @param {number} damSireId - The ID of the dam/sire to fetch document progress for.
     * @param {string} status - The status of the documents to filter by (e.g., 'IN_PROGRESS', 'UPLOADED', 'ERROR').
     * @returns {Promise<Object>} A promise that resolves to the document progress details fetched via the API.
     * @throws {Error} Throws an error if the API request fails.
    */
    async getDamSireDocumentsListProgress(damSireId, status) {
        return DamSireAPI.getDamSireDocumentsListProgress(damSireId, status);
    },

    /**
     * Deletes a dam/sire document from the server.
     * 
     * @async
     * @function deleteDamSireDocument
     * @param {string} documentId - The ID of the document to delete.
     * @param {Object} documentDetails - The details of the document being deleted.
     * @param {string} documentDetails.damSireId - The ID of the dam or sire associated with the document.
     * @returns {Promise<Object>} - The server response from the delete operation.
     * @throws {Error} If the API request fails.
     */
    async deleteDamSireDocument(documentId, documentDetails) {
        return DamSireAPI.deleteDamSireDocument(documentId, documentDetails);
    },

    /**
     * Updates a dam/sire document from the server.
     * 
     * @async
     * @function updateDamSireDocument
     * @param {string} documentId - The ID of the document to update.
     * @param {Object} documentDetails - The details of the document being updated.
     * @param {string} documentDetails.description - The descsription of the dam or sire associated with the document.
     * @returns {Promise<Object>} - The server response from the update operation.
     * @throws {Error} If the API request fails.
     */
    async updateDamSireDocument(documentId, documentDetails) {
        return DamSireAPI.updateDamSireDocument(documentId, documentDetails);
    },
};
